/**
 * PowerPoint Template Engine
 *
 * Parses .pptx files as ZIP archives (Open XML format) and manipulates
 * the XML to clone slide layouts and inject artefact content into
 * placeholder shapes.
 *
 * PPTX structure:
 *   ppt/presentation.xml      — slide list and relationships
 *   ppt/slides/slide1.xml     — individual slides
 *   ppt/slides/_rels/slide1.xml.rels — slide relationships
 *   ppt/slideLayouts/         — layout definitions
 *   ppt/slideMasters/         — master slide definitions
 *   [Content_Types].xml       — content type registry
 */

import type { Artefact } from "@/types/artefacts";

import PizZip from "pizzip";

import { logger } from "@/telemetry";
import { ArtefactType } from "@/types/artefacts";
import { stripMarkdown } from "@/utils/templateEngine/templateUtils";

/** Minimal image data needed to embed a captured HTML artefact as an image */
export interface PptxImageData {
  /** Binary PNG data */
  imageData: Uint8Array;
  /** Original capture width in pixels */
  width: number;
  /** Original capture height in pixels */
  height: number;
}

export interface PptxTemplateData {
  title?: string;
  date?: string;
  author?: string;
  brand?: string;
  /** Font family for injected text (from Export Font setting). Falls back to theme font. */
  fontFamily?: string;
  artefacts: Array<{
    id: string;
    title: string;
    content: string;
    type: ArtefactType;
  }>;
  /** Pre-captured images for HTML artefacts, keyed by artefact ID */
  imageMap?: Map<string, PptxImageData>;
}

// ============================================================================
// TABLE CONSTANTS — match the non-template export (markdownToPptx.ts)
// ============================================================================
const MAX_TABLE_ROWS_PER_SLIDE = 6;

/** Row height in EMU (approx 0.45 inch per row) — 914400 EMU = 1 inch */
const TABLE_ROW_HEIGHT_EMU = Math.round(0.45 * 914400);

/** Font sizes in hundredths of a point */
const TABLE_HEADER_FONT_SIZE = 1200; // 12pt
const TABLE_BODY_FONT_SIZE = 1100; // 11pt

/**
 * Build the OOXML font elements for run properties.
 * Uses an explicit font family when provided (from Export Font setting),
 * otherwise falls back to theme font references (+mj-lt / +mn-lt).
 */
function buildFontXml(fontFamily?: string, isTitle?: boolean): string {
  const latin = fontFamily || (isTitle ? "+mj-lt" : "+mn-lt");
  const cs = fontFamily || (isTitle ? "+mj-cs" : "+mn-cs");

  return `<a:latin typeface="${escapeXml(latin)}"/><a:cs typeface="${escapeXml(cs)}"/>`;
}

/** Parse a markdown table string into header cells and body rows */
function parseMarkdownTable(markdown: string): { headers: string[]; rows: string[][] } {
  const lines = markdown
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("|"));

  if (lines.length < 2) return { headers: [], rows: [] };

  const splitRow = (line: string): string[] =>
    line
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());

  const headers = splitRow(lines[0]);

  // Skip separator line (index 1)
  const rows = lines.slice(2).map(splitRow);

  return { headers, rows };
}

/**
 * Build an OOXML `<a:tbl>` element from parsed table data.
 * This produces a native, editable PowerPoint table.
 */
function buildTableXml(
  headers: string[],
  bodyRows: string[],
  colCount: number,
  bounds: PlaceholderBounds,
  fontFamily?: string,
): string {
  const colWidth = Math.round(bounds.cx / colCount);
  const fontXml = buildFontXml(fontFamily, false);

  const gridCols = Array(colCount)
    .fill(0)
    .map(() => `<a:gridCol w="${colWidth}"/>`)
    .join("");

  const buildCell = (text: string, isHeader: boolean): string => {
    const fontSize = isHeader ? TABLE_HEADER_FONT_SIZE : TABLE_BODY_FONT_SIZE;
    const bold = isHeader ? ' b="1"' : "";
    const fontColour = isHeader ? "FFFFFF" : "363636";
    const fill = isHeader
      ? '<a:solidFill><a:srgbClr val="28326E"/></a:solidFill>'
      : '<a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill>';

    return `<a:tc>
  <a:txBody><a:bodyPr/><a:lstStyle/>
    <a:p><a:r><a:rPr lang="en-GB" sz="${fontSize}"${bold} dirty="0"><a:solidFill><a:srgbClr val="${fontColour}"/></a:solidFill>${fontXml}</a:rPr><a:t>${escapeXml(text)}</a:t></a:r></a:p>
  </a:txBody>
  <a:tcPr>${fill}<a:lnL w="12700"><a:solidFill><a:srgbClr val="CCCCCC"/></a:solidFill></a:lnL><a:lnR w="12700"><a:solidFill><a:srgbClr val="CCCCCC"/></a:solidFill></a:lnR><a:lnT w="12700"><a:solidFill><a:srgbClr val="CCCCCC"/></a:solidFill></a:lnT><a:lnB w="12700"><a:solidFill><a:srgbClr val="CCCCCC"/></a:solidFill></a:lnB></a:tcPr>
</a:tc>`;
  };

  const buildRow = (cells: string[], isHeader: boolean): string => {
    const cellsXml = cells.map((c) => buildCell(c, isHeader)).join("");

    return `<a:tr h="${TABLE_ROW_HEIGHT_EMU}">${cellsXml}</a:tr>`;
  };

  // Build header row(s)
  const headerRowXml = buildRow(headers, true);

  // Build body rows — bodyRows here is a flat array of cell values
  // We need to chunk them by colCount
  const dataRows: string[][] = [];

  for (let i = 0; i < bodyRows.length; i += colCount) {
    dataRows.push(bodyRows.slice(i, i + colCount));
  }

  const bodyRowsXml = dataRows.map((row) => buildRow(row, false)).join("");

  return `<a:tbl>
  <a:tblPr firstRow="1" bandRow="1"><a:tblStyle val="{5C22544A-7EE6-4342-B048-85BDC9FD1C3A}"/></a:tblPr>
  <a:tblGrid>${gridCols}</a:tblGrid>
  ${headerRowXml}${bodyRowsXml}
</a:tbl>`;
}

/**
 * Build a `<p:graphicFrame>` wrapping an OOXML table.
 * This replaces a body placeholder with a native table.
 */
function buildGraphicFrameXml(tableXml: string, bounds: PlaceholderBounds): string {
  return `<p:graphicFrame>
  <p:nvGraphicFramePr>
    <p:cNvPr id="1001" name="Table"/>
    <p:cNvGraphicFramePr><a:graphicFrameLocks noGrp="1"/></p:cNvGraphicFramePr>
    <p:nvPr/>
  </p:nvGraphicFramePr>
  <p:xfrm>
    <a:off x="${bounds.x}" y="${bounds.y}"/>
    <a:ext cx="${bounds.cx}" cy="${bounds.cy}"/>
  </p:xfrm>
  <a:graphic>
    <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/table">
      ${tableXml}
    </a:graphicData>
  </a:graphic>
</p:graphicFrame>`;
}

/**
 * Replace the body/subtitle placeholder in a slide with a native OOXML table.
 */
function replaceBodyPlaceholderWithTable(
  slideXml: string,
  headers: string[],
  bodyRows: string[][],
  fontFamily?: string,
): string {
  let replaced = false;

  return slideXml.replace(/<p:sp>[\s\S]*?<\/p:sp>/g, (spBlock) => {
    if (replaced) return spBlock;

    const phMatch = spBlock.match(/<p:ph[^>]*type="([^"]*)"[^>]*\/>/);

    if (!phMatch) return spBlock;

    const phType = phMatch[1];

    if (phType !== "body" && phType !== "subTitle") return spBlock;

    replaced = true;
    const bounds = getPlaceholderBounds(spBlock) || DEFAULT_BODY_BOUNDS;
    const colCount = headers.length;
    const flatCells = bodyRows.flat();
    const tableXml = buildTableXml(headers, flatCells, colCount, bounds, fontFamily);

    return buildGraphicFrameXml(tableXml, bounds);
  });
}

/** Escape XML special characters in text values */
function escapeXml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/**
 * Build an OOXML text body (`<p:txBody>`) with the given text.
 * Splits on newlines to create separate `<a:p>` paragraphs.
 */
function buildTextBody(
  text: string,
  fontSize?: number,
  isTitle?: boolean,
  fontFamily?: string,
): string {
  const lines = text.split("\n").filter((line) => line.trim() !== "");
  const fontAttr = fontSize ? ` sz="${fontSize * 100}"` : "";
  const fontXml = buildFontXml(fontFamily, isTitle);

  const paragraphs = lines
    .map(
      (line) =>
        `<a:p><a:r><a:rPr lang="en-GB" dirty="0"${fontAttr}>${fontXml}</a:rPr><a:t>${escapeXml(line)}</a:t></a:r></a:p>`,
    )
    .join("");

  return `<p:txBody><a:bodyPr/><a:lstStyle/>${paragraphs || '<a:p><a:endParaRPr lang="en-GB"/></a:p>'}</p:txBody>`;
}

/**
 * Inject text into PPTX placeholder shapes identified by `<p:ph>` type attributes.
 *
 * Real PPTX templates have empty `<p:txBody>` elements inside `<p:sp>` shapes.
 * The "Click to add text" prompt comes from the slide layout, not the slide XML.
 *
 * Strategy: extract each `<p:sp>...</p:sp>` block, check its `<p:ph>` type,
 * and replace the `<p:txBody>` with actual content.
 */
function injectTextIntoSlide(
  xml: string,
  replacements: {
    title?: string;
    content?: string;
    extraReplacements?: Record<string, string>;
    fontFamily?: string;
  },
): string {
  let result = xml;

  // Strategy 1: Process each <p:sp> block individually
  result = result.replace(/<p:sp>[\s\S]*?<\/p:sp>/g, (spBlock) => {
    // Check what type of placeholder this shape is
    const phMatch = spBlock.match(/<p:ph[^>]*type="([^"]*)"[^>]*\/>/);

    if (!phMatch) return spBlock; // Not a placeholder shape, leave as-is

    const phType = phMatch[1];

    // Title placeholder → inject title
    if ((phType === "title" || phType === "ctrTitle") && replacements.title) {
      const newBody = buildTextBody(replacements.title, 28, true, replacements.fontFamily);

      return spBlock.replace(/<p:txBody>[\s\S]*?<\/p:txBody>/, newBody);
    }

    // Body/subtitle placeholder → inject content
    if ((phType === "body" || phType === "subTitle") && replacements.content) {
      const newBody = buildTextBody(replacements.content, 14, false, replacements.fontFamily);

      return spBlock.replace(/<p:txBody>[\s\S]*?<\/p:txBody>/, newBody);
    }

    // Footer placeholder → replace known tokens in existing text
    if (phType === "ftr" && replacements.extraReplacements) {
      let updated = spBlock;

      for (const [token, value] of Object.entries(replacements.extraReplacements)) {
        // Replace within <a:t> elements
        const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`(<a:t[^>]*>)([^<]*${escaped}[^<]*)(</a:t>)`, "g");

        updated = updated.replace(regex, (_m, open, text, close) => {
          return `${open}${text.replace(token, escapeXml(value))}${close}`;
        });
      }

      return updated;
    }

    return spBlock;
  });

  // Strategy 2: Direct PLACEHOLDER replacement in any remaining <a:t> elements
  const allReplacements: Record<string, string> = {
    ...replacements.extraReplacements,
  };

  if (replacements.title) allReplacements["TITLE"] = replacements.title;
  if (replacements.content) allReplacements["CONTENT"] = replacements.content;

  for (const [placeholder, value] of Object.entries(allReplacements)) {
    const escapedValue = escapeXml(value);

    for (const pattern of [`{{${placeholder}}}`, `{${placeholder}}`]) {
      const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(<a:t[^>]*>)([^<]*${escaped}[^<]*)(</a:t>)`, "g");

      result = result.replace(regex, (_match, openTag, textContent, closeTag) => {
        return `${openTag}${textContent.replace(pattern, escapedValue)}${closeTag}`;
      });
    }
  }

  // Strategy 3: Literal exact-match replacement for bare placeholder words.
  // Many real-world templates use plain text like AUTHOR / DATE / TITLE inside
  // placeholder text frames (no curly braces). Only replace when the entire
  // <a:t> element content equals the token (optional surrounding whitespace),
  // so we never clobber legitimate content that happens to contain the word.
  for (const [token, value] of Object.entries(allReplacements)) {
    if (!value) continue;
    const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(<a:t[^>]*>)\\s*${escapedToken}\\s*(</a:t>)`, "g");

    result = result.replace(regex, (_match, openTag, closeTag) => {
      return `${openTag}${escapeXml(value)}${closeTag}`;
    });
  }

  return result;
}

/**
 * Get the list of slide filenames from presentation.xml.
 * Returns an ordered array of relationship IDs.
 */
function getSlideRelIds(presentationXml: string): string[] {
  const matches = [...presentationXml.matchAll(/<p:sldId[^>]*r:id="([^"]+)"[^>]*\/>/g)];

  return matches.map((m) => m[1]);
}

/**
 * Get slide filename from a relationship ID by reading presentation.xml.rels.
 */
function getSlideFilenameFromRel(relsXml: string, relId: string): string | null {
  const regex = new RegExp(`<Relationship[^>]*Id="${relId}"[^>]*Target="([^"]+)"[^>]*/>`);
  const match = relsXml.match(regex);

  return match ? match[1] : null;
}

/**
 * Find the highest slide number currently in the zip.
 */
function getMaxSlideNumber(zip: PizZip): number {
  let max = 0;

  const slideFiles = zip.file(/^ppt\/slides\/slide\d+\.xml$/);

  for (const file of slideFiles) {
    const match = file.name.match(/slide(\d+)\.xml$/);

    if (match) {
      const num = parseInt(match[1], 10);

      if (num > max) max = num;
    }
  }

  return max;
}

/**
 * Find the highest relationship ID number in the presentation rels.
 */
function getMaxRelId(relsXml: string): number {
  const matches = [...relsXml.matchAll(/Id="rId(\d+)"/g)];
  let max = 0;

  for (const m of matches) {
    const num = parseInt(m[1], 10);

    if (num > max) max = num;
  }

  return max;
}

/**
 * Detect which slide in the template is the "content" slide
 * (i.e. not the title/cover slide). Heuristic: the second slide,
 * or if only one slide exists, use that one.
 */
function findContentSlideIndex(slideCount: number): number {
  return slideCount > 1 ? 1 : 0; // 0-indexed; prefer second slide
}

// EMU (English Metric Units) constants for OOXML positioning
// 1 inch = 914400 EMU
const EMU_PER_INCH = 914400;

/** Position and size in EMU */
interface PlaceholderBounds {
  x: number;
  y: number;
  cx: number;
  cy: number;
}

/** Default body placeholder bounds for a standard 10"×7.5" slide */
const DEFAULT_BODY_BOUNDS: PlaceholderBounds = {
  x: Math.round(0.5 * EMU_PER_INCH), // 0.5" left margin
  y: Math.round(1.75 * EMU_PER_INCH), // Below title area
  cx: Math.round(9 * EMU_PER_INCH), // 9" wide
  cy: Math.round(5 * EMU_PER_INCH), // 5" tall
};

/**
 * Extract position bounds from a `<p:sp>` XML block.
 * Returns null if the shape has no explicit transform (inherits from layout).
 */
function getPlaceholderBounds(spBlock: string): PlaceholderBounds | null {
  const offMatch = spBlock.match(/<a:off\s+x="(\d+)"\s+y="(\d+)"/);
  const extMatch = spBlock.match(/<a:ext\s+cx="(\d+)"\s+cy="(\d+)"/);

  if (!offMatch || !extMatch) return null;

  return {
    x: parseInt(offMatch[1], 10),
    y: parseInt(offMatch[2], 10),
    cx: parseInt(extMatch[1], 10),
    cy: parseInt(extMatch[2], 10),
  };
}

/**
 * Build OOXML `<p:pic>` element for an embedded image.
 * The image is centred within the given bounds while preserving aspect ratio.
 */
function buildPicXml(
  relId: string,
  bounds: PlaceholderBounds,
  imageWidthPx: number,
  imageHeightPx: number,
): string {
  const aspectRatio = imageWidthPx / imageHeightPx;
  const boundsRatio = bounds.cx / bounds.cy;

  let finalCx: number;
  let finalCy: number;

  if (aspectRatio > boundsRatio) {
    // Wider than bounds — constrain by width
    finalCx = bounds.cx;
    finalCy = Math.round(bounds.cx / aspectRatio);
  } else {
    // Taller than bounds — constrain by height
    finalCy = bounds.cy;
    finalCx = Math.round(bounds.cy * aspectRatio);
  }

  // Centre within the placeholder area
  const offsetX = bounds.x + Math.round((bounds.cx - finalCx) / 2);
  const offsetY = bounds.y + Math.round((bounds.cy - finalCy) / 2);

  return `<p:pic>
  <p:nvPicPr>
    <p:cNvPr id="1001" name="Chart Image"/>
    <p:cNvPicPr><a:picLocks noChangeAspect="1"/></p:cNvPicPr>
    <p:nvPr/>
  </p:nvPicPr>
  <p:blipFill>
    <a:blip r:embed="${relId}"/>
    <a:stretch><a:fillRect/></a:stretch>
  </p:blipFill>
  <p:spPr>
    <a:xfrm>
      <a:off x="${offsetX}" y="${offsetY}"/>
      <a:ext cx="${finalCx}" cy="${finalCy}"/>
    </a:xfrm>
    <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
  </p:spPr>
</p:pic>`;
}

/**
 * Replace the body/subtitle placeholder in a slide with a `<p:pic>` image element.
 * Falls back to default positioning if the placeholder has no explicit bounds.
 */
function replaceBodyPlaceholderWithImage(
  slideXml: string,
  imageRelId: string,
  imageWidthPx: number,
  imageHeightPx: number,
): string {
  let replaced = false;

  const result = slideXml.replace(/<p:sp>[\s\S]*?<\/p:sp>/g, (spBlock) => {
    if (replaced) return spBlock; // Only replace first body placeholder

    const phMatch = spBlock.match(/<p:ph[^>]*type="([^"]*)"[^>]*\/>/);

    if (!phMatch) return spBlock;

    const phType = phMatch[1];

    if (phType !== "body" && phType !== "subTitle") return spBlock;

    replaced = true;
    const bounds = getPlaceholderBounds(spBlock) || DEFAULT_BODY_BOUNDS;

    return buildPicXml(imageRelId, bounds, imageWidthPx, imageHeightPx);
  });

  return result;
}

/**
 * Apply a PowerPoint template to artefact data.
 *
 * Strategy:
 * 1. Open template as ZIP
 * 2. Identify the title slide and content slide
 * 3. Replace placeholders in the title slide ({{TITLE}}, {{DATE}}, etc.)
 * 4. For each artefact, clone the content slide and inject content
 * 5. Update presentation.xml and [Content_Types].xml
 * 6. Output as Blob
 *
 * @param templateBuffer - The .pptx template file as ArrayBuffer
 * @param data - Template data with artefacts to inject
 * @returns Blob of the rendered .pptx presentation
 */
export async function applyPptxTemplate(
  templateBuffer: ArrayBuffer,
  data: PptxTemplateData,
): Promise<Blob> {
  try {
    const zip = new PizZip(templateBuffer);

    // Read presentation structure
    const presentationXml = zip.file("ppt/presentation.xml")?.asText();
    const presentationRels = zip.file("ppt/_rels/presentation.xml.rels")?.asText();

    if (!presentationXml || !presentationRels) {
      throw new Error("Invalid PPTX: missing presentation.xml or relationships");
    }

    // Get existing slides
    const slideRelIds = getSlideRelIds(presentationXml);
    const slideFilenames: string[] = [];

    for (const relId of slideRelIds) {
      const filename = getSlideFilenameFromRel(presentationRels, relId);

      if (filename) {
        slideFilenames.push(filename.replace(/^\//, ""));
      }
    }

    if (slideFilenames.length === 0) {
      throw new Error("Invalid PPTX: no slides found");
    }

    // Replace placeholders in the title slide (first slide)
    const titleSlideFile = `ppt/${slideFilenames[0]}`;
    const titleSlideXml = zip.file(titleSlideFile)?.asText();

    if (titleSlideXml) {
      // For the title slide we deliberately omit `content` so the body
      // placeholder isn't clobbered as a whole — that would wipe DATE when
      // we only want to replace AUTHOR. Strategy 3 (literal token match)
      // inside `injectTextIntoSlide` handles AUTHOR and DATE individually.
      const updatedTitleSlide = injectTextIntoSlide(titleSlideXml, {
        title: data.title || "",
        fontFamily: data.fontFamily,
        extraReplacements: {
          TITLE: data.title || "",
          DATE: data.date || new Date().toLocaleDateString(navigator.language),
          AUTHOR: data.author || "",
          BRAND: data.brand || "",
          "Insert document title": data.title || "",
          "Insert date": data.date || new Date().toLocaleDateString(navigator.language),
        },
      });

      zip.file(titleSlideFile, updatedTitleSlide);
    }

    // Identify the content slide template
    const contentSlideIdx = findContentSlideIndex(slideFilenames.length);
    const contentSlideFile = `ppt/${slideFilenames[contentSlideIdx]}`;
    const contentSlideXml = zip.file(contentSlideFile)?.asText();
    const contentSlideRelsFile = `ppt/slides/_rels/${slideFilenames[contentSlideIdx].replace("slides/", "")}.rels`;
    const contentSlideRels = zip.file(contentSlideRelsFile)?.asText();

    if (!contentSlideXml) {
      throw new Error("Cannot read content slide template");
    }

    // Common footer replacements applied to every cloned content slide
    const footerReplacements: Record<string, string> = {
      TITLE: data.title || "",
      DATE: data.date || new Date().toLocaleDateString(navigator.language),
      AUTHOR: data.author || "",
      BRAND: data.brand || "",
    };

    // Clone the content slide for each artefact
    let maxSlideNum = getMaxSlideNumber(zip);
    let maxRelId = getMaxRelId(presentationRels);
    let updatedPresentationXml = presentationXml;
    let updatedPresentationRels = presentationRels;
    let contentTypesXml = zip.file("[Content_Types].xml")?.asText() || "";

    // Convert template content types (.potx/.pptm/.potm) to presentation (.pptx)
    // so the output file opens correctly in PowerPoint
    contentTypesXml = contentTypesXml
      .replace(
        /application\/vnd\.openxmlformats-officedocument\.presentationml\.template\.main\+xml/g,
        "application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml",
      )
      .replace(
        /application\/vnd\.ms-powerpoint\.presentation\.macroEnabled\.main\+xml/g,
        "application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml",
      )
      .replace(
        /application\/vnd\.ms-powerpoint\.template\.macroEnabled\.main\+xml/g,
        "application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml",
      );

    // Find the last <p:sldId> to determine the next id attribute value
    const sldIdMatches = [...presentationXml.matchAll(/<p:sldId[^>]*id="(\d+)"[^>]*\/>/g)];
    let maxSldId = 256; // OOXML default start

    for (const m of sldIdMatches) {
      const num = parseInt(m[1], 10);

      if (num > maxSldId) maxSldId = num;
    }

    // Remove the original content slide if we have artefacts to replace it
    // (Keep the title slide and any other slides before the content slide)
    const newSlides: Array<{ num: number; relId: string }> = [];

    // Ensure PNG content type is registered (needed for image embedding)
    if (data.imageMap?.size && !contentTypesXml.includes('Extension="png"')) {
      contentTypesXml = contentTypesXml.replace(
        "</Types>",
        '<Default Extension="png" ContentType="image/png"/></Types>',
      );
    }

    for (const artefact of data.artefacts) {
      maxSlideNum++;
      maxRelId++;
      maxSldId++;

      const newSlideNum = maxSlideNum;
      const newRelId = `rId${maxRelId}`;
      const newSlideFilename = `slide${newSlideNum}.xml`;

      const hasImage = data.imageMap?.has(artefact.id);
      // HTML artefacts with images → embed as picture; TABLE artefacts → native table
      const isImageArtefact = artefact.type === ArtefactType.HTML && hasImage;
      const isTableArtefact = artefact.type === ArtefactType.TABLE;

      let clonedSlideXml: string;
      let slideRels = contentSlideRels || "";

      if (isTableArtefact) {
        // Parse markdown table into headers + body rows
        const { headers, rows: allBodyRows } = parseMarkdownTable(artefact.content);

        if (headers.length === 0) {
          // Fallback: not a valid markdown table — inject as plain text
          const content = stripMarkdown(artefact.content);

          clonedSlideXml = injectTextIntoSlide(contentSlideXml, {
            title: artefact.title,
            content,
            fontFamily: data.fontFamily,
            extraReplacements: {
              ...footerReplacements,
              TITLE: artefact.title,
              CONTENT: content,
              "Slide Title": artefact.title,
              "Click to add title": artefact.title,
              "Click to add text": content,
            },
          });
        } else {
          // Determine how many rows fit on the first slide
          const firstChunk = allBodyRows.slice(0, MAX_TABLE_ROWS_PER_SLIDE);
          const remaining = allBodyRows.slice(MAX_TABLE_ROWS_PER_SLIDE);

          // First slide with table
          clonedSlideXml = injectTextIntoSlide(contentSlideXml, {
            title: artefact.title,
            fontFamily: data.fontFamily,
            extraReplacements: {
              ...footerReplacements,
              TITLE: artefact.title,
              "Slide Title": artefact.title,
              "Click to add title": artefact.title,
            },
          });

          clonedSlideXml = replaceBodyPlaceholderWithTable(
            clonedSlideXml,
            headers,
            firstChunk,
            data.fontFamily,
          );

          // Ensure a: namespace for table elements
          if (!clonedSlideXml.includes("xmlns:a=")) {
            clonedSlideXml = clonedSlideXml.replace(
              "<p:sld",
              '<p:sld xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"',
            );
          }

          // Write first slide
          zip.file(`ppt/slides/${newSlideFilename}`, clonedSlideXml);

          if (slideRels) {
            zip.file(`ppt/slides/_rels/${newSlideFilename}.rels`, slideRels);
          }

          if (!contentTypesXml.includes(`/ppt/slides/${newSlideFilename}`)) {
            contentTypesXml = contentTypesXml.replace(
              "</Types>",
              `<Override PartName="/ppt/slides/${newSlideFilename}" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slide+xml"/></Types>`,
            );
          }

          updatedPresentationRels = updatedPresentationRels.replace(
            "</Relationships>",
            `<Relationship Id="${newRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide" Target="slides/${newSlideFilename}"/></Relationships>`,
          );

          newSlides.push({ num: newSlideNum, relId: newRelId });

          // Create continuation slides for remaining rows
          let rowIdx = 0;

          while (rowIdx < remaining.length) {
            maxSlideNum++;
            maxRelId++;
            maxSldId++;

            const contSlideNum = maxSlideNum;
            const contRelId = `rId${maxRelId}`;
            const contSlideFilename = `slide${contSlideNum}.xml`;

            const chunk = remaining.slice(rowIdx, rowIdx + MAX_TABLE_ROWS_PER_SLIDE);

            let contSlideXml = injectTextIntoSlide(contentSlideXml, {
              title: `${artefact.title} (continued)`,
              fontFamily: data.fontFamily,
              extraReplacements: {
                ...footerReplacements,
                TITLE: `${artefact.title} (continued)`,
                "Slide Title": `${artefact.title} (continued)`,
                "Click to add title": `${artefact.title} (continued)`,
              },
            });

            contSlideXml = replaceBodyPlaceholderWithTable(
              contSlideXml,
              headers,
              chunk,
              data.fontFamily,
            );

            if (!contSlideXml.includes("xmlns:a=")) {
              contSlideXml = contSlideXml.replace(
                "<p:sld",
                '<p:sld xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"',
              );
            }

            zip.file(`ppt/slides/${contSlideFilename}`, contSlideXml);

            if (contentSlideRels) {
              zip.file(`ppt/slides/_rels/${contSlideFilename}.rels`, contentSlideRels);
            }

            if (!contentTypesXml.includes(`/ppt/slides/${contSlideFilename}`)) {
              contentTypesXml = contentTypesXml.replace(
                "</Types>",
                `<Override PartName="/ppt/slides/${contSlideFilename}" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slide+xml"/></Types>`,
              );
            }

            updatedPresentationRels = updatedPresentationRels.replace(
              "</Relationships>",
              `<Relationship Id="${contRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide" Target="slides/${contSlideFilename}"/></Relationships>`,
            );

            newSlides.push({ num: contSlideNum, relId: contRelId });
            rowIdx += MAX_TABLE_ROWS_PER_SLIDE;
          }

          // Skip the common write/register logic below — already handled above
          continue;
        }
      } else if (isImageArtefact) {
        const image = data.imageMap!.get(artefact.id)!;

        // Inject title into the cloned template slide
        clonedSlideXml = injectTextIntoSlide(contentSlideXml, {
          title: artefact.title,
          fontFamily: data.fontFamily,
          extraReplacements: {
            ...footerReplacements,
            TITLE: artefact.title,
            "Slide Title": artefact.title,
            "Click to add title": artefact.title,
          },
        });

        // Replace the body placeholder with the chart image
        const imageMediaFilename = `chart_${artefact.id.replace(/[^a-zA-Z0-9]/g, "_")}.png`;
        const imageRelId = "rIdImg1";

        clonedSlideXml = replaceBodyPlaceholderWithImage(
          clonedSlideXml,
          imageRelId,
          image.width,
          image.height,
        );

        // Ensure r: namespace is declared for image embedding
        if (!clonedSlideXml.includes("xmlns:r=")) {
          clonedSlideXml = clonedSlideXml.replace(
            "<p:sld",
            '<p:sld xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"',
          );
        }

        // Store image binary in the zip
        zip.file(`ppt/media/${imageMediaFilename}`, image.imageData);

        // Build slide rels with image reference appended
        const baseRels =
          contentSlideRels ||
          `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>`;

        slideRels = baseRels.replace(
          "</Relationships>",
          `<Relationship Id="${imageRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/${imageMediaFilename}"/></Relationships>`,
        );
      } else {
        // Text artefact — inject text as before
        const content =
          artefact.type === ArtefactType.CODE ? artefact.content : stripMarkdown(artefact.content);

        clonedSlideXml = injectTextIntoSlide(contentSlideXml, {
          title: artefact.title,
          content,
          fontFamily: data.fontFamily,
          extraReplacements: {
            ...footerReplacements,
            TITLE: artefact.title,
            CONTENT: content,
            "Slide Title": artefact.title,
            "Click to add title": artefact.title,
            "Click to add text": content,
          },
        });
      }

      // Write the new slide
      zip.file(`ppt/slides/${newSlideFilename}`, clonedSlideXml);

      // Write slide relationships
      if (slideRels) {
        zip.file(`ppt/slides/_rels/${newSlideFilename}.rels`, slideRels);
      }

      // Register in Content_Types
      if (!contentTypesXml.includes(`/ppt/slides/${newSlideFilename}`)) {
        contentTypesXml = contentTypesXml.replace(
          "</Types>",
          `<Override PartName="/ppt/slides/${newSlideFilename}" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slide+xml"/></Types>`,
        );
      }

      // Add relationship to presentation.xml.rels
      updatedPresentationRels = updatedPresentationRels.replace(
        "</Relationships>",
        `<Relationship Id="${newRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide" Target="slides/${newSlideFilename}"/></Relationships>`,
      );

      newSlides.push({ num: newSlideNum, relId: newRelId });
    }

    // Add new slide references to presentation.xml
    // Insert after the last existing <p:sldId> element
    const lastSldIdMatch = updatedPresentationXml.match(/<p:sldId[^>]*\/>\s*<\/p:sldIdLst>/);

    if (lastSldIdMatch) {
      const newSldIdEntries = newSlides
        .map(
          (s) =>
            `<p:sldId id="${maxSldId - newSlides.length + newSlides.indexOf(s) + 1}" r:id="${s.relId}"/>`,
        )
        .join("");

      updatedPresentationXml = updatedPresentationXml.replace(
        /<\/p:sldIdLst>/,
        `${newSldIdEntries}</p:sldIdLst>`,
      );
    }

    // Optionally remove the original content slide template
    // (only if it's different from the title slide and we have artefacts)
    if (data.artefacts.length > 0 && contentSlideIdx > 0) {
      // Remove the original content slide file
      zip.remove(contentSlideFile);

      if (contentSlideRels) {
        zip.remove(contentSlideRelsFile);
      }

      // Remove its reference from presentation.xml
      const originalRelId = slideRelIds[contentSlideIdx];

      if (originalRelId) {
        // Remove <p:sldId> entry
        updatedPresentationXml = updatedPresentationXml.replace(
          new RegExp(`<p:sldId[^>]*r:id="${originalRelId}"[^>]*/>\\s*`),
          "",
        );

        // Remove relationship entry
        updatedPresentationRels = updatedPresentationRels.replace(
          new RegExp(`<Relationship[^>]*Id="${originalRelId}"[^>]*/>`),
          "",
        );
      }
    }

    // Write updated XML files back to ZIP
    zip.file("ppt/presentation.xml", updatedPresentationXml);
    zip.file("ppt/_rels/presentation.xml.rels", updatedPresentationRels);
    zip.file("[Content_Types].xml", contentTypesXml);

    const output = zip.generate({
      type: "blob",
      mimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    });

    logger.info("PPTX template applied successfully", {
      artefactCount: data.artefacts.length,
      title: data.title,
    });

    return output;
  } catch (error) {
    logger.error("Failed to apply PPTX template", { error: String(error) });
    throw new Error(
      `Failed to apply PPTX template: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * Prepare PPTX template data from artefacts.
 */
export function preparePptxTemplateData(
  artefacts: Artefact[],
  options: {
    title?: string;
    author?: string;
    brand?: string;
    locale?: string;
    fontFamily?: string;
    imageMap?: Map<string, PptxImageData>;
  } = {},
): PptxTemplateData {
  return {
    title: options.title || "",
    date: new Date().toLocaleDateString(options.locale || navigator.language, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    author: options.author || "",
    brand: options.brand || "",
    fontFamily: options.fontFamily,
    artefacts: artefacts.map((a) => ({
      id: a.id,
      title: a.title,
      content: a.content,
      type: a.type,
    })),
    imageMap: options.imageMap,
  };
}
